import React, { useState, useRef, useEffect } from 'react';
import { MagnifyingGlassIcon, ArrowPathIcon, Squares2X2Icon, Bars3Icon, ArrowsPointingOutIcon, ArrowsPointingInIcon, ChevronDownIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

// Tipos para as props do componente
interface SegmentedControlOption {
  value: string;
  label: string;
  icon: React.ReactNode;
  title: string;
}

interface SegmentedControlProps {
  value: string;
  onChange: (value: string) => void;
  options: SegmentedControlOption[];
}

interface ToolbarFilters {
  q: string;
  fRegiao: string[];
  fConf: string;
  fEmpresas: string[];
}

interface ToolbarSetFilters {
  setQ: (value: string) => void;
  setFRegiao: (value: string[]) => void;
  setFConf: (value: string) => void;
  setFEmpresas: (value: string[]) => void;
}

interface EmpresaOption {
  codigo: string;
  label: string;
}

interface ToolbarProps {
  filters: ToolbarFilters;
  setFilters: ToolbarSetFilters;
  regioes: string[];
  empresas: EmpresaOption[];
  onRefresh: () => void;
  layout: string;
  setLayout: (layout: string) => void;
  autorefresh: boolean;
  setAutorefresh: (enabled: boolean) => void;
  isFullscreen: boolean;
  setIsFullscreen: (enabled: boolean) => void;
}

// Componente MultiSelect para seleção múltipla (genérico — usa label/value pra
// suportar tanto strings simples como empresas com código+nome).
interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  headerLabel?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  selected,
  onChange,
  placeholder = "Selecione...",
  headerLabel = "Itens",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleOption = (value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange([]);
  };

  const selectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(options.map(o => o.value));
  };

  // Resolve label da 1a selecionada pra exibir resumido no botão.
  const firstSelectedLabel = selected.length === 1
    ? (options.find(o => o.value === selected[0])?.label ?? selected[0])
    : null;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          "flex items-center gap-1 rounded-md border bg-white px-2 md:px-3 py-2 text-xs md:text-sm transition",
          "hover:border-zinc-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500",
          "dark:bg-zinc-950 dark:hover:border-zinc-600",
          selected.length > 0
            ? "border-blue-500 dark:border-blue-500"
            : "border-zinc-300 dark:border-zinc-700"
        )}
      >
        <span className="max-w-[80px] md:max-w-[120px] truncate">
          {selected.length === 0
            ? placeholder
            : firstSelectedLabel
            ? firstSelectedLabel
            : `${selected.length} selecionadas`}
        </span>
        {selected.length > 0 ? (
          <XMarkIcon
            className="h-3.5 w-3.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            onClick={clearAll}
          />
        ) : (
          <ChevronDownIcon
            className={clsx("h-3.5 w-3.5 text-zinc-400 transition-transform", isOpen && "rotate-180")}
          />
        )}
      </button>

      {isOpen && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-md border border-zinc-300 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
          <div className="flex items-center justify-between border-b border-zinc-200 px-3 py-2 dark:border-zinc-700">
            <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
              {headerLabel}
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-xs text-blue-600 hover:underline dark:text-blue-400"
              >
                Todas
              </button>
              <button
                onClick={clearAll}
                className="text-xs text-zinc-500 hover:underline dark:text-zinc-400"
              >
                Limpar
              </button>
            </div>
          </div>

          <div className="max-h-[200px] overflow-y-auto py-1">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={(e) => toggleOption(option.value, e)}
                className={clsx(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors",
                  selected.includes(option.value)
                    ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                    : "text-zinc-700 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                )}
              >
                <div
                  className={clsx(
                    "flex h-4 w-4 items-center justify-center rounded border transition-colors flex-shrink-0",
                    selected.includes(option.value)
                      ? "border-blue-600 bg-blue-600 text-white"
                      : "border-zinc-300 dark:border-zinc-600"
                  )}
                >
                  {selected.includes(option.value) && <CheckIcon className="h-3 w-3" />}
                </div>
                <span className="truncate">{option.label}</span>
              </button>
            ))}
            {options.length === 0 && (
              <div className="px-3 py-2 text-xs text-zinc-500">Nenhuma opção</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para o controle segmentado (Horizontal/Grid)
const SegmentedControl: React.FC<SegmentedControlProps> = ({ value, onChange, options }) => {
  return (
    <div className="flex items-center rounded-md border border-zinc-300 bg-white p-0.5 dark:border-zinc-700 dark:bg-zinc-950">
      {options.map((opt) => (
        <button
          key={opt.value}
          onClick={() => onChange(opt.value)}
          className={clsx(
            "flex items-center justify-center gap-1 md:gap-2 rounded-md px-2 md:px-3 py-1.5 text-xs md:text-sm transition-colors",
            value === opt.value
              ? "bg-zinc-800 text-white shadow-sm"
              : "text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
          )}
          title={opt.title}
        >
          {opt.icon}
          <span className="hidden sm:inline">{opt.label}</span>
        </button>
      ))}
    </div>
  );
};

const Toolbar: React.FC<ToolbarProps> = ({
  filters,
  setFilters,
  regioes,
  empresas,
  onRefresh,
  layout,
  setLayout,
  autorefresh,
  setAutorefresh,
  isFullscreen,
  setIsFullscreen,
}) => {
  const { q, fRegiao, fConf, fEmpresas } = filters;
  const { setQ, setFRegiao, setFConf, setFEmpresas } = setFilters;

  const regiaoOptions = regioes.map(r => ({ value: r, label: r }));
  const empresaOptions = empresas.map(e => ({ value: e.codigo, label: e.label }));

  return (
    <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
      {/* --- LADO ESQUERDO: BUSCA E FILTROS --- */}
      <div className="flex flex-1 items-center gap-2 md:gap-3 min-w-0">
        {/* Campo de Busca Modernizado */}
        <div className="relative flex-1">
          <MagnifyingGlassIcon
            className="absolute left-3 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-zinc-400"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por cliente, vendedor, cidade..."
            className="w-full rounded-md border border-zinc-300 bg-white py-2 pl-10 pr-4 text-sm transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-950 dark:focus:border-blue-500"
          />
        </div>

        {/* Filtros Redesenhados */}
        <div className="flex items-center gap-1 md:gap-2">
          <MultiSelect
            options={empresaOptions}
            selected={fEmpresas}
            onChange={setFEmpresas}
            placeholder="Empresas"
            headerLabel="Empresas"
          />

          <MultiSelect
            options={regiaoOptions}
            selected={fRegiao}
            onChange={setFRegiao}
            placeholder="Regiões"
            headerLabel="Regiões"
          />

          <select
            value={fConf}
            onChange={(e) => setFConf(e.target.value)}
            className="rounded-md border border-zinc-300 bg-white px-2 md:px-3 py-2 text-xs md:text-sm transition hover:border-zinc-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-zinc-700 dark:bg-zinc-950 dark:hover:border-zinc-600"
          >
            <option value="">Status</option>
            <option value="SIM">✅ Conferido</option>
            <option value="NAO">🟠 Pendente</option>
            <option value="PARCIAL">🔴 Parcial</option>
          </select>
        </div>
      </div>

      {/* --- LADO DIREITO: CONTROLES DE EXIBIÇÃO E AÇÕES --- */}
      <div className="flex items-center gap-2 md:gap-3">
        {/* Controle Segmentado para Layout */}
        <SegmentedControl
          value={layout}
          onChange={setLayout}
          options={[
            {
              value: "horizontal",
              label: "Kanban",
              icon: <Bars3Icon className="h-3.5 w-3.5" />,
              title: "Visualização em colunas",
            },
            {
              value: "vertical",
              label: "Grade",
              icon: <Squares2X2Icon className="h-3.5 w-3.5" />,
              title: "Visualização em grade",
            },
          ]}
        />

        {/* Ações */}
        <div className="flex items-center gap-1 md:gap-2">
          <label className="flex cursor-pointer items-center gap-1 md:gap-2 text-xs md:text-sm text-zinc-600 dark:text-zinc-300">
            <input
              type="checkbox"
              className="h-3 w-3 md:h-4 md:w-4 rounded accent-blue-600"
              checked={autorefresh}
              onChange={(e) => setAutorefresh(e.target.checked)}
            />
            <span className="hidden sm:inline">Auto-refresh</span>
            <span className="sm:hidden">Auto</span>
          </label>
          <button
            onClick={onRefresh}
            className="flex items-center justify-center rounded-md border border-zinc-300 bg-white p-1.5 md:p-2 text-zinc-700 transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-300 dark:hover:bg-zinc-800"
            title="Atualizar agora"
          >
            <ArrowPathIcon className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className="flex items-center justify-center rounded-md border border-zinc-300 bg-white p-1.5 md:p-2 text-zinc-700 transition hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-300 dark:hover:bg-zinc-800"
            title={isFullscreen ? "Sair da Tela Cheia" : "Modo Tela Cheia"}
          >
            {isFullscreen ? <ArrowsPointingInIcon className="h-3.5 w-3.5" /> : <ArrowsPointingOutIcon className="h-3.5 w-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toolbar;